/**
 * Trocar de filial com o carrinho cheio — a regra, pura, sem React e sem rede.
 *
 * ## O defeito que este modulo existe para fechar
 *
 * Depois de trocar de filial, o cabecalho mostrava a nova e `GET /v1/cart` continuava
 * devolvendo a antiga. Nada na tela dizia isso. O contrato ja previa o conflito
 * (`409 CART_STORE_MISMATCH`, `API §2.2`) e o servidor ja o emitia — quem estava calado era a
 * interface, e o preco que a pessoa via no cabecalho nao era o preco pelo qual ela ia pagar.
 *
 * ## A regra nao e nossa, e ela ja estava escrita em tres lugares
 *
 * | Documento | O que manda |
 * | --- | --- |
 * | `DOMAIN` INV-C1 | "Uma filial por carrinho [...] trocar de filial exige `SetStore`, que **revalida todas as linhas e devolve a lista de linhas perdidas**" |
 * | `DOMAIN` RN-076 | "Trocar de filial revalida tudo e **informa perdas**" |
 * | `API §6.9` | "No MVP, trocar de filial **recria o espelho via `PUT /v1/cart`**" |
 *
 * Disso saem as tres funcoes deste arquivo, e nada alem: montar o corpo do `PUT`
 * (`corpoDaTrocaDeFilial`), descobrir a divergencia (`carrinhoDivergeDaFilial`) e comparar o
 * antes com o depois para nomear a perda (`diferencaDoCarrinho`).
 *
 * ## Por que a perda so e conhecida DEPOIS do `PUT`
 *
 * Nao ha ensaio. `GET /v1/catalog/availability` responde por **`sku`**, e `CartItem` nao tem
 * `sku` — tem `productId`. Nao existe rota que responda "o que a filial B faria com este
 * carrinho" sem escrever nada, e `PUT /v1/cart/store` (a rota que devolveria o diff como aviso)
 * e fase `P`, adiada por `CONTRACTS §8.2`. Entao a interface faz o que da para fazer com
 * honestidade: **antes**, avisa que a perda pode acontecer e nomeia os itens em risco (que sao
 * todos); **depois**, nomeia exatamente os que sairam. O `DESIGN_SYSTEM §8` proibe esconder a
 * perda — nao proibe descobri-la em dois tempos.
 *
 * // DUVIDA: com `PUT /v1/cart/store` implementada (fase P), o passo de confirmacao poderia
 * // nomear os itens perdidos ANTES da escrita, porque o `API §6.9` diz que ela "devolve
 * // `CART_STORE_MISMATCH` como aviso com o diff de preco/disponibilidade". Enquanto ela nao
 * // existe, a comparacao aqui e a unica fonte da lista de perdas no cliente.
 *
 * ## A comparacao e por `productId`, e nao pelos `warnings` do servidor
 *
 * O servidor manda `warnings[]` com `code` e `productId` (`reconcile.ts`), e a tela mostra
 * essas mensagens — elas sao do catalogo fechado e nao se inventa texto por cima delas. Mas a
 * lista de **perdas** e derivada da diferenca entre as duas respostas de carrinho, e nao dos
 * avisos: um aviso que deixasse de ser emitido por um defeito de servidor faria a tela dizer
 * "nada mudou" sobre um carrinho que encolheu. A diferenca nao tem como mentir — ela compara o
 * que estava com o que ficou.
 */

import type { CartItem, CartItemIntent, CartResponse, ReplaceCartRequest } from '@farmacia/contracts';

/**
 * Uma linha que mudou na troca. `para === 0` significa **saiu do carrinho**.
 *
 * O nome vem do carrinho de ANTES: o item que saiu nao existe mais na resposta nova, e sem
 * guardar o nome a tela so poderia dizer "um item saiu" — que e exatamente o "alguns itens"
 * que o `DESIGN_SYSTEM §8` recusa.
 */
export interface MudancaDeLinha {
  readonly productId: string;
  readonly displayName: string;
  readonly de: number;
  readonly para: number;
}

export interface DiferencaDoCarrinho {
  /** Linhas que a filial nova nao vende: sumiram por completo. */
  readonly perdidos: readonly MudancaDeLinha[];
  /** Linhas que sobreviveram com quantidade menor (INV-C5, `QUANTITY_LIMIT_EXCEEDED`). */
  readonly reduzidos: readonly MudancaDeLinha[];
}

/** Houve qualquer coisa a contar? */
export function houveMudanca(diferenca: DiferencaDoCarrinho): boolean {
  return diferenca.perdidos.length > 0 || diferenca.reduzidos.length > 0;
}

/**
 * A filial do carrinho e a filial escolhida no cabecalho sao a mesma?
 *
 * Devolve `false` quando falta informacao — sem carrinho, ou sem filial escolhida, nao ha
 * divergencia a anunciar, e um aviso disparado por dado ausente e um aviso que ninguem entende.
 */
export function carrinhoDivergeDaFilial(
  carrinho: CartResponse | undefined,
  storeIdSelecionado: string | null,
): boolean {
  if (carrinho === undefined || storeIdSelecionado === null) return false;
  return carrinho.storeId !== storeIdSelecionado;
}

/** As linhas do carrinho viradas em intencao — o corpo que `PUT /v1/cart` aceita. */
export function intencoesDoCarrinho(carrinho: CartResponse): readonly CartItemIntent[] {
  return carrinho.items.map((item) => ({ productId: item.productId, quantity: item.quantity }));
}

/**
 * O corpo de `PUT /v1/cart` que **troca a filial** (`API §6.9`).
 *
 * `fulfillmentMode` vai junto de proposito: o `ReplaceCartRequestSchema` o trata como opcional,
 * e omiti-lo faria a troca de filial redefinir tambem a modalidade — trocar de loja nao pode
 * transformar em silencio uma retirada em entrega (INV-C2).
 *
 * `updatedAt` e o relogio do DISPOSITIVO, e e o desempate de conteudo de `PWA §8.1`
 * ("ultimo-escreve-vence por `updatedAt`"). Ele e injetavel para o teste nao depender de
 * `Date.now()`.
 */
export function corpoDaTrocaDeFilial(
  carrinho: CartResponse,
  storeIdAlvo: string,
  agora: Date = new Date(),
): ReplaceCartRequest {
  return {
    storeId: storeIdAlvo,
    updatedAt: agora.toISOString(),
    fulfillmentMode: carrinho.fulfillmentMode,
    items: [...intencoesDoCarrinho(carrinho)],
  };
}

/**
 * O que a troca custou: compara as linhas de antes com as de depois, por `productId`.
 *
 * Um item que sumiu entra em `perdidos` com `para: 0`. Um item que ficou com quantidade menor
 * entra em `reduzidos`. Quantidade MAIOR nao existe nesta operacao (a reconciliacao so corta) e
 * por isso nao tem categoria: se aparecer, e sinal de que o corpo enviado nao era o carrinho.
 */
export function diferencaDoCarrinho(
  antes: readonly CartItem[],
  depois: readonly CartItem[],
): DiferencaDoCarrinho {
  const porProduto = new Map(depois.map((item) => [item.productId, item] as const));
  const perdidos: MudancaDeLinha[] = [];
  const reduzidos: MudancaDeLinha[] = [];

  for (const item of antes) {
    const sobrevivente = porProduto.get(item.productId);
    if (sobrevivente === undefined) {
      perdidos.push({
        productId: item.productId,
        displayName: item.displayName,
        de: item.quantity,
        para: 0,
      });
      continue;
    }
    if (sobrevivente.quantity < item.quantity) {
      reduzidos.push({
        productId: item.productId,
        // O nome do DEPOIS: e o mesmo produto, mas e a resposta nova que manda no que a tela
        // escreve — o nome de vitrine e curado por filial (`API §6.20.2`).
        displayName: sobrevivente.displayName,
        de: item.quantity,
        para: sobrevivente.quantity,
      });
    }
  }

  return { perdidos, reduzidos };
}
